// Based on https://schema.org/Person JSON-LD structure
function getAuthorLDSchema(user) {
  return {
    '@context': 'https://schema.org/',
    '@type': 'Person',
    name: user.username,
  };
}

// Based on https://schema.org/Review JSON-LD structure
function getReviewLDSchema(review) {
  return {
    '@context': 'https://schema.org/',
    '@type': 'Review',
    author: getAuthorLDSchema(review.user),
    datePublished: review.submit_date,
    reviewBody: review.comment_escaped,
    name: review.title_escaped,
    reviewRating: {
      '@type': 'Rating',
      ratingValue: review.rating.toString(),
      bestRating: '5',
      worstRating: '1',
    },
  };
}

// Based on https://schema.org/Product JSON-LD structure
function getProductDetailSchema(deal) {
  const endDate = deal.dates.end_date ? deal.dates.end_date : deal.dates.schema_end_date;

  const productLDSchema = {
    '@context': 'http://schema.org/',
    '@type': 'Product',
    name: deal.public_name,
    brand: {
      '@type': 'Brand',
      name: deal.public_name,
    },
    mpn: deal.id.toString(),
    sku: deal.id.toString(),
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: deal.deal_review.average_rating?.toString() || '0',
      reviewCount: deal.deal_review.review_count?.toString() || '0',
    },
    review: deal.top_5_reviews.map(getReviewLDSchema),
    offers: {
      '@type': 'Offer',
      priceCurrency: 'USD',
      url: `https://appsumo.com${deal.get_absolute_url}`,
      price: deal.default_price.toString(),
      availabilityStarts: deal.dates.start_date ? deal.dates.start_date : '',
      availabilityEnds: endDate,
      priceValidUntil: endDate,
      availability: deal.is_redeemable ? 'http://schema.org/InStock' : 'http://schema.org/SoldOut',
      seller: {
        '@type': 'Organization',
        name: 'AppSumo',
      },
    },
  };

  if (deal.products[0]?.story?.subheader) {
    productLDSchema.description = deal.products[0]?.story?.subheader;
  }

  if (deal.media_url) {
    productLDSchema.image = {
      '@type': 'ImageObject',
      url: deal.media_url,
      height: '266',
      width: '800',
    };
  }

  return productLDSchema;
}

// Based on https://schema.org/Question JSON-LD structure
function getQuestionLDSchema(question, deal) {
  return {
    '@context': 'https://schema.org/',
    '@type': 'Question',
    name: question.comment_escaped,
    text: question.comment_escaped,
    answerCount: question.children.length,
    upvoteCount: question.num_likes,
    author: getAuthorLDSchema(question.user),
    discussionUrl: question.display_path,
    isBasedOn: getProductDetailSchema(deal),
  };
}

// Based on https://schema.org/Answer JSON-LD structure
function getAnswerLDSchema(answer, questionPath) {
  return {
    '@context': 'https://schema.org/',
    '@type': 'Answer',
    text: answer.comment_escaped,
    upvoteCount: answer.num_likes,
    url: `${questionPath}/#${answer.id}`,
    author: getAuthorLDSchema(answer.user),
  };
}

// Based on https://schema.org/Product#eg-0010 JSON-LD structure
export function getProductPageLDSchemaJSONString(deal) {
  return JSON.stringify(getProductDetailSchema(deal), null, '\t');
}

// Based on https://schema.org/Review JSON-LD structure
// https://developers.google.com/search/docs/advanced/structured-data/review-snippet#examples
export function getReviewPageLDSchemeJSONString(review, deal) {
  const reviewSchema = getReviewLDSchema(review);

  // add info of the Product this review belongs
  reviewSchema.itemReviewed = getProductDetailSchema(deal);

  return JSON.stringify(reviewSchema, null, '\t');
}

// https:// developers.google.com/search/docs/advanced/structured-data/qapage?hl=en#question
export function getQuestionPageLDSchemeJSONString(question, deal) {
  const questionSchema = {
    '@context': 'https://schema.org',
    '@type': 'QAPage',
    mainEntity: getQuestionLDSchema(question, deal),
  };

  if (question.children.length) {
    questionSchema.mainEntity.suggestedAnswer = [
      ...question.children.map((answer) => getAnswerLDSchema(answer, question.display_path)),
    ];
  }

  return JSON.stringify(questionSchema, null, '\t');
}

export default {
  getProductPageLDSchemaJSONString,
  getReviewPageLDSchemeJSONString,
  getQuestionPageLDSchemeJSONString,
};
